/**
 * Object-URL port — small browser seam for `URL.createObjectURL` /
 * `URL.revokeObjectURL`.
 *
 * Per ACT-INDEEP-B2-B-PROFILE-MEDIA-FRONTEND01 §14 the FE preview
 * lifecycle must be verifiable from tests without globally mocking
 * business logic. This module exposes a single object URL port
 * whose default implementation calls the browser's
 * `URL.createObjectURL` / `URL.revokeObjectURL` and whose
 * production wiring is the natural one — there is no
 * "fake" persistence, no module singleton, no localStorage.
 *
 * The browser-side object URL is process-local and never durable
 * (FAKE_DURABILITY = 0).
 */

export interface ObjectUrlPort {
  /**
   * Allocate an object URL for the given file/blob and return the
   * URL string. The caller owns the URL and is expected to revoke
   * it via {@link revokeObjectURL} when the preview is no longer
   * needed (success, cancel, teardown).
   */
  createObjectURL(file: Blob): string;
  /**
   * Release the object URL. Calling on an unknown URL is a no-op.
   */
  revokeObjectURL(url: string): void;
}

export const browserObjectUrlPort: ObjectUrlPort = {
  createObjectURL(file) {
    return URL.createObjectURL(file);
  },
  revokeObjectURL(url) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore — revoke is best-effort and never throws on unknown ids
    }
  },
};

/**
 * The default port the FE wires into the questionnaire program.
 * Tests may pass a different port (typically a recording stub) via
 * the harness environment.
 */
export const defaultObjectUrlPort: ObjectUrlPort = browserObjectUrlPort;
